import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Clock, MapPin, TimerOff, PencilLine } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { CorrectEndDialog } from "@/components/dashboard/CorrectEndDialog";

function fmtEnd(iso) {
  try { return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

function ReportRow({ r, canEdit, onEdit }) {
  const navigate = useNavigate();
  return (
    <div data-testid={`open-report-${r.intervention_id}`}
      className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-md bg-[#1a160e] border border-[#FFB020]/25 px-4 py-2.5 hover:border-[#FFB020]/60 transition-colors">
      <button onClick={() => navigate("/dashboard/suivi-gardiennage")} data-testid={`open-report-link-${r.intervention_id}`}
        className="flex flex-wrap items-center gap-x-4 gap-y-1 text-left flex-1 min-w-0">
        <span className="text-white font-medium">{r.agent_name || "—"}</span>
        <span className="text-zinc-400 text-sm flex items-center gap-1"><MapPin className="w-3.5 h-3.5 shrink-0" /> {r.societe_nom ? `${r.societe_nom} — ` : ""}{r.site_nom || "—"}</span>
        <span className="ml-auto text-[#FFB020] font-code text-sm flex items-center gap-1"><Clock className="w-3.5 h-3.5 shrink-0" /> {r.auto_closed ? "Fin retenue" : "Fin prévue"} {fmtEnd(r.planned_end)}</span>
      </button>
      <span data-testid={`open-report-status-${r.intervention_id}`}
        className={`text-[11px] font-semibold uppercase tracking-wide border rounded px-1.5 py-0.5 ${r.auto_closed ? "text-zinc-300 border-zinc-500/50" : "text-[#FFB020] border-[#FFB020]/50"}`}>
        {r.auto_closed ? "Clôturé automatiquement" : "Toujours ouvert"}
      </span>
      {r.auto_closed && canEdit && (
        <button onClick={() => onEdit(r)} data-testid={`open-report-correct-${r.intervention_id}`}
          className="flex items-center gap-1 text-xs font-medium text-[#007AFF] border border-[#007AFF]/40 rounded px-2 py-1 hover:bg-[#007AFF]/10 transition-colors">
          <PencilLine className="w-3.5 h-3.5" /> Corriger l'heure de fin
        </button>
      )}
    </div>
  );
}

export const OpenReportsBanner = ({ items }) => {
  const { user } = useAuth();
  const [done, setDone] = useState([]);
  const [editing, setEditing] = useState(null);
  const list = (items || []).filter((i) => !done.includes(i.intervention_id));
  if (!list.length) return null;
  const open = list.filter((i) => !i.auto_closed).length;
  const canEdit = ["admin", "manager"].includes(user?.role);
  return (
    <div data-testid="open-reports-banner" className="mb-8 rounded-lg border border-[#FFB020]/40 bg-[#FFB020]/10 p-5 fade-up">
      <div className="flex items-center gap-2 mb-3">
        <TimerOff className="w-5 h-5 text-[#FFB020] shrink-0" />
        <h2 className="font-display text-lg font-semibold text-[#FFB020]" data-testid="open-reports-title">Fins de service non validées</h2>
      </div>
      <p className="text-zinc-400 text-sm mb-3">
        {open > 0 ? `${open} compte${open > 1 ? "s" : ""} rendu${open > 1 ? "s" : ""} encore ouvert${open > 1 ? "s" : ""} après la fin prévue. ` : ""}
        Sans validation de l'agent, le service est clôturé automatiquement à l'heure de fin prévue (30 min après). Corrigez l'heure si l'agent a fini plus tôt ou plus tard.
      </p>
      <div className="space-y-2">
        {list.map((r) => <ReportRow key={r.intervention_id} r={r} canEdit={canEdit} onEdit={setEditing} />)}
      </div>
      {editing && (
        <CorrectEndDialog report={editing} onClose={() => setEditing(null)}
          onDone={(id) => { setDone((d) => [...d, id]); setEditing(null); }} />
      )}
    </div>
  );
};
